import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import styles from './Tabs.module.css';

export type TabsProps = {
  items: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
  /**
   * Gives every tab the id `<idPrefix>-tab-<key>` and `aria-controls="<idPrefix>-panel-<key>"`, so
   * the caller can label its tabpanel with `aria-labelledby`.
   */
  idPrefix?: string;
};

export function Tabs({ items, value, onChange, idPrefix }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (items.length === 0) return;
    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;
    event.preventDefault();
    const target = items[next];
    if (target === undefined) return;
    onChange(target.key);
    refs.current[target.key]?.focus();
  };

  return (
    <div className={styles['tabs']} role="tablist">
      {items.map((item, index) => {
        const selected = item.key === value;
        return (
          <button
            key={item.key}
            ref={(el) => {
              refs.current[item.key] = el;
            }}
            type="button"
            role="tab"
            id={idPrefix !== undefined ? `${idPrefix}-tab-${item.key}` : undefined}
            aria-controls={idPrefix !== undefined ? `${idPrefix}-panel-${item.key}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={[styles['tab'], selected ? styles['selected'] : ''].filter(Boolean).join(' ')}
            onClick={() => onChange(item.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
